import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppButton,
  Box,
  List,
  PaginationBar,
  SearchPlaceholderIcon,
  SelectDropdown,
  Text,
  useResponsive,
} from '@common-fe/common-fe';

import { SORT_OPTIONS } from '@/common/constants';
import ExcessContributionWithdrawModal from '@/components/modals/ExcessContributionWithdrawModal/ExcessContributionWithdrawModal';
import { useExcessContributionAvailable } from '@/components/modals/ExcessContributionWithdrawModal/useExcessContributionAvailable';
import { CompleteVerificationMessage } from '@/modules/core/components/Dashboard/components/BannersList/CompleteBanner';
import { useMyEnrollments } from '@/modules/transaction';
import dropdownStateAllToggling, {
  checkIsAllOptionSelected,
} from '@/utils/handlers/dropdownStateAllToggling';

import useSearchParams from './hooks/useSearchParams';
import useHighlightedTaxFormStore from './stores/useHighlightedTaxForm.store';
import { useTaxFormsListQuery } from './queries';
import TaxFormsItem from './TaxFormsItem';

interface Props {
  isStandAloneMode?: boolean;
}
const TaxForms: React.FC<Props> = ({ isStandAloneMode }) => {
  const { isExcessContributionAvailable } = useExcessContributionAvailable();
  const { isMobile } = useResponsive();
  const [isModalVisible, setModalVisible] = useState(false);
  const { t } = useTranslation();
  const { highlightedTaxFormId } = useHighlightedTaxFormStore();

  const { yearOptions, page, perPage, setPerPage, setPage, sortParams, setSortParams, years, setYears } =
    useSearchParams();
  const yearsValues = useMemo(() => dropdownStateAllToggling(years), [years]);
  const isAllYearsSelected = useMemo(() => checkIsAllOptionSelected(years), [years]);
  const { isOpenHSAExist } = useMyEnrollments();

  const { total, isLoading, formattedData } = useTaxFormsListQuery({
    page: page - 1,
    perPage,
    sortBy: sortParams,
    years: isAllYearsSelected ? [] : yearsValues,
  });

  return (
    <Box>
      {isStandAloneMode && (
        <Box margin={{ bottom: 'spacing24' }}>
          <CompleteVerificationMessage />
        </Box>
      )}

      {isModalVisible ? <ExcessContributionWithdrawModal onSetVisible={setModalVisible} isEditMode /> : null}

      <Box
        direction="column"
        margin={{ bottom: 'spacing24' }}
        pad="spacing24"
        round="container1Round"
        background="module"
        data-testid="tax-forms_content"
      >
        <Box direction="row" margin={{ bottom: 'spacing24' }} align="center" justify="between">
          <Text size="large" weight={700} data-testid="tax-form_total-title">
            {t('Files:')} {total}
          </Text>
          <Box direction="row">
            <Box margin={{ left: 'spacing12' }} width="180px">
              <SelectDropdown
                testId="tax-form_year"
                id="years"
                name="Years"
                prefix="Years"
                values={yearsValues}
                onChangeValues={setYears}
                options={yearOptions}
                activeTitle
                allMode
                ellipsisMode
              />
            </Box>
            <Box margin={{ left: 'spacing12' }} width="180px">
              <SelectDropdown
                id="sort_by"
                name="Sort by"
                prefix="Sort by"
                testId="tax-form_sort"
                value={sortParams}
                onChange={setSortParams}
                options={SORT_OPTIONS}
                activeTitle
                singleMode
              />
            </Box>
            {isExcessContributionAvailable && isOpenHSAExist && !isMobile && !isStandAloneMode ? (
              <Box margin={{ left: 'spacing24' }}>
                <AppButton
                  testId="withdrawExcessContributions"
                  width="250px"
                  onClick={() => setModalVisible(true)}
                >
                  Withdraw Excess Contributions
                </AppButton>
              </Box>
            ) : null}
          </Box>
        </Box>
        {isExcessContributionAvailable && isOpenHSAExist && isMobile && !isStandAloneMode ? (
          <Box width="100%" direction="row" justify="end" margin={{ bottom: 'spacing24' }}>
            <AppButton
              testId="withdrawExcessContributions"
              width="250px"
              onClick={() => setModalVisible(true)}
            >
              Withdraw Excess Contributions
            </AppButton>
          </Box>
        ) : null}

        <List>
          <List.Header>
            <List.Header.Item>Form name</List.Header.Item>
          </List.Header>
        </List>
        {isLoading ? (
          <List.Preloader count={3} />
        ) : (
          <>
            {formattedData?.length ? null : (
              <Box
                direction="column"
                data-testid="tax-form_placeholder"
                pad={{ top: 'l', bottom: 'l' }}
                justify="center"
                align="center"
              >
                <SearchPlaceholderIcon size="72px" color="iconSecondary" />
                <Text weight={700} margin={{ top: 'spacing24' }}>
                  {t('You have no tax forms for the tax year selected.')}
                </Text>
                <Text margin={{ top: 's' }}>
                  {t(
                    'Forms are only created if there is spending or contribution activity during the tax year.'
                  )}
                </Text>
                <Text>
                  {t(
                    ' Form 1099-SA is available in January to report spending activity and 5498-SA in May to report any contributions made.'
                  )}
                </Text>
              </Box>
            )}
            {formattedData.map((item) => (
              <TaxFormsItem
                key={item.id}
                isOpenHSAExist={isOpenHSAExist}
                {...item}
                highlightedTaxFormId={highlightedTaxFormId}
              />
            ))}
          </>
        )}

        <PaginationBar
          testId="tax-forms_pagination"
          page={page}
          total={total}
          pageSize={perPage}
          onChangePage={setPage}
          onChangePageSize={setPerPage}
        />
      </Box>
    </Box>
  );
};

export default TaxForms;
